import React, { useContext } from 'react';
import classes from './../BulkImportContent/BulkImportContent.module.scss';
import { UseFormReturn } from 'react-hook-form';
import { BulkActionsContext } from '../states/BulkActionsProvider';
import { CheckboxWithAction } from '../../../components/actions/CheckboxWithAction';
import classNames from 'classnames';
import { RadioButtons } from '../../../components/forms/RadioButtons';
import { FormElementAction } from '../../../models/basic-objects';
import { PostQueuePosition } from '../../../models/graphql';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { Link } from 'react-router-dom';
import { ExpireAfterDate } from '../Schedule/ExpireAfterDate';
import { ExpireAfterCount } from '../Schedule/ExpireAfterCount';
import { Toggle } from '../../../components/forms/Toggle';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type BulkImportSchedulingViewProps = {
  //
  formInstance: UseFormReturn;
  setExpireAfterDateHandler: FormElementAction<Date>;
  expiryDate: Date | null;
};

export const BulkImportSchedulingView = (props: BulkImportSchedulingViewProps): JSX.Element => {
  const { formInstance, setExpireAfterDateHandler, expiryDate } = props;
  const { state, dispatch } = useContext(BulkActionsContext);
  const { beacon } = useContext(BeaconContext);

  const { scheduling } = state;
  const { checkboxes, content } = scheduling;
  const { positionInQueue, evergreenRecycle } = content;

  const { active: evergreenState, expireType, expiryMaxRepeats } = evergreenRecycle;

  const setQueuePositionHandler: FormElementAction = (value, complete) => {
    complete();
    const position = value === PostQueuePosition.TOP ? PostQueuePosition.TOP : PostQueuePosition.BOTTOM;
    dispatch({ type: 'setPositionInQueue', payload: position });
  };

  const toggleEvergreenAction: ToggleAction = (_, complete) => {
    complete();
    dispatch({ type: 'toggleEvergreen' });
  };

  const { positionInQueue: positionInQueueCheckbox, evergreenRecycle: evergreenRecycleCheckbox } = checkboxes;

  const isHtmlInputElementGuard = (el: EventTarget): el is HTMLInputElement => {
    return el.hasOwnProperty('checked');
  };

  return (
    <>
      {/* Position in Queue */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'positionInQueueCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'scheduling', key: 'positionInQueue' } });
            }}
            wrapper={true}
            checked={positionInQueueCheckbox}
            label={'POSITION IN QUEUE'}
          />
        </div>
        <div className={classNames(classes.InnerBlock)}>
          <div className={'cell shrink'}>
            <RadioButtons
              displayType={'block'}
              formInstance={formInstance}
              items={[
                { label: 'Top of Queue', value: PostQueuePosition.TOP },
                { label: 'Bottom of Queue', value: PostQueuePosition.BOTTOM }
              ]}
              name={'positionInQueueRadio'}
              rules={{}}
              size={'small'}
              action={setQueuePositionHandler}
              value={positionInQueue}
              disabled={!positionInQueueCheckbox}
            />
            <BoxNotification type={'info'}>
              Automatically schedule your Post based on the weekly timeslots in your{' '}
              <Link to={'/publish/plan'} target={'_blank'}>
                Posting Plan
              </Link>
              . Choosing &apos;Top of Queue&apos; publishes it at the next available timeslot, while &apos;Bottom of
              Queue&apos; places it after existing scheduled content.{' '}
            <a onClick={() => beacon('article', '693', { type: 'modal' })}>Learn more</a>.
            </BoxNotification>
          </div>
        </div>
      </div>
      {/* Evergreen */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'evergreenCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'scheduling', key: 'evergreenRecycle' } });
            }}
            wrapper={true}
            checked={evergreenRecycleCheckbox}
            label={'EVERGREEN RECYCLING'}
          />
        </div>
        <div className={classNames(classes.InnerBlock)}>
          <div className={'cell shrink'}>
            <div className={'grid-x'}>
              <div className={'cell small-12'}>
                <Toggle
                  disabled={!evergreenRecycleCheckbox}
                  checked={evergreenState}
                  action={toggleEvergreenAction}
                  formInstance={formInstance}
                  name={'evergreenToggle'}
                  rules={{}}
                  label={'Evergreen Recycling'}
                  wrapper={true}
                />
              </div>
              <div className={'cell small-12 align-middle'}>
                {/* TODO add checkeboxes */}
                <div className={'grid-x align-middle'}>
                  <div className={'cell shrink'}>
                    <CheckboxWithAction
                      checked={expireType.includes('date')}
                      value={'date'}
                      label={'Expire after'}
                      name={'evergreenExpirySettings'}
                      size={'small'}
                      onClickHandler={event => {
                        if (!isHtmlInputElementGuard(event.target)) return;
                        dispatch({
                          type: 'setExpiryType',
                          payload: {
                            expireType: 'date',
                            action: event.target.checked ? 'add' : 'remove'
                          }
                        });
                      }}
                      disabled={!evergreenRecycleCheckbox || !evergreenState}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <ExpireAfterDate
                      calendarUpdateAction={setExpireAfterDateHandler}
                      formInstance={formInstance}
                      name={'expireAfterDate'}
                      disabled={!expireType.includes('date') || !evergreenRecycleCheckbox || !evergreenState}
                      {...(expiryDate ? { date: expiryDate } : {})}
                    />
                  </div>
                </div>
                <div className={'grid-x align-middle margin-top-small-16'}>
                  <div className={'cell shrink'}>
                    <CheckboxWithAction
                      checked={expireType.includes('count')}
                      value={'count'}
                      name={'evergreenExpirySettings'}
                      size={'small'}
                      label={'Expire after'}
                      onClickHandler={event => {
                        if (!isHtmlInputElementGuard(event.target)) return;
                        dispatch({
                          type: 'setExpiryType',
                          payload: {
                            expireType: 'count',
                            action: event.target.checked ? 'add' : 'remove'
                          }
                        });
                      }}
                      disabled={!evergreenRecycleCheckbox || !evergreenState}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <ExpireAfterCount
                      formInstance={formInstance}
                      name={'expireAfterCount'}
                      disabled={!expireType.includes('count') || !evergreenRecycleCheckbox || !evergreenState}
                      {...(expiryMaxRepeats ? { value: expiryMaxRepeats } : {})}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
